const REDUCED_MOTION_MQ = '(prefers-reduced-motion: reduce)'
const DEFAULT_AUTOPLAY_DELAY = 3000
const DEFAULT_SLIDES_PER_VIEW = 2
const DEFAULT_SLIDES_PER_VIEW_MOBILE = 1.6

function parseSlidesPerView(value, fallback) {
  const parsed = parseFloat(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

const FOCUSABLE_SELECTOR =
  'a[href], button, input, select, textarea, [tabindex]'
const TABINDEX_STORE = 'a11yTabindex' // dataset key -> data-a11y-tabindex
const PENDING_HIDE_STORE = 'a11yPendingHide' // dataset key -> data-a11y-pending-hide
const FOCUSOUT_BOUND_STORE = 'a11yFocusoutBound' // dataset key -> data-a11y-focusout-bound

function focusablesIn(el) {
  return Array.prototype.slice.call(el.querySelectorAll(FOCUSABLE_SELECTOR))
}

/**
 * Take a slide out of / put it back into the accessibility tree and tab order.
 * Swiper's bundled a11y module labels the carousel and nav but does not hide
 * off-screen slides, so we do it here: `inert` neutralises focusable
 * descendants (incl. those in shadow DOM), and the explicit tabindex handling
 * covers light-DOM focusables for older engines.
 */
function setSlideHidden(slide, hidden) {
  // Never trap focus: if the slide is about to be hidden while it still holds
  // focus, defer hiding until focus leaves it. Otherwise a focused card that
  // gets swiped off-screen would keep aria-hidden/inert off and stay in the
  // tab order while invisible.
  if (hidden && slide.contains(document.activeElement)) {
    slide.dataset[PENDING_HIDE_STORE] = 'true'
    if (!slide.dataset[FOCUSOUT_BOUND_STORE]) {
      slide.dataset[FOCUSOUT_BOUND_STORE] = 'true'
      slide.addEventListener('focusout', function onFocusOut(event) {
        // Ignore focus moves that stay within the slide.
        if (slide.contains(event.relatedTarget)) return
        slide.removeEventListener('focusout', onFocusOut)
        delete slide.dataset[FOCUSOUT_BOUND_STORE]
        if (slide.dataset[PENDING_HIDE_STORE] === 'true') {
          delete slide.dataset[PENDING_HIDE_STORE]
          setSlideHidden(slide, true)
        }
      })
    }
    return
  }

  // A newer request to show the slide cancels any deferred hide.
  if (!hidden) delete slide.dataset[PENDING_HIDE_STORE]

  if (hidden) {
    slide.setAttribute('aria-hidden', 'true')
    slide.setAttribute('inert', '')
    focusablesIn(slide).forEach((node) => {
      if (!(TABINDEX_STORE in node.dataset)) {
        node.dataset[TABINDEX_STORE] = node.getAttribute('tabindex') || ''
      }
      node.setAttribute('tabindex', '-1')
    })
  } else {
    slide.setAttribute('aria-hidden', 'false')
    slide.removeAttribute('inert')
    focusablesIn(slide).forEach((node) => {
      if (!(TABINDEX_STORE in node.dataset)) return
      const original = node.dataset[TABINDEX_STORE]
      if (original === '') node.removeAttribute('tabindex')
      else node.setAttribute('tabindex', original)
      delete node.dataset[TABINDEX_STORE]
    })
  }
}

class STLProductCarousel extends HTMLElement {
  constructor() {
    super()
  }

  connectedCallback() {
    this.swiper = null
    this.carouselEl = this.querySelector('.stl-product-card-carousel')
    this.regionEl = this.querySelector('[data-stl-pc-region]')
    this.nextBtn = this.querySelector('.swiper-nav-btn-next')
    this.prevBtn = this.querySelector('.swiper-nav-btn-prev')
    this.instructions = this.querySelector('.stl-pc-instructions')

    this.autoplayEnabled = this.dataset.enableAutoplay === 'true'
    this.isInView = false
    this.isHovered = false
    this.hasFocus = false
    this.userStopped = false

    this.init()
    this.initAutoplay()
  }

  disconnectedCallback() {
    this.observer?.disconnect()
    this.reducedMotion?.removeEventListener('change', this.onReducedMotionChange)
    this.swiper?.destroy(true, true)
  }

  init() {
    // Single-product layouts render a standalone card, not a Swiper carousel.
    if (!this.carouselEl) return

    const updateVisibility = (swiper) => {
      this.updateSlideVisibility(swiper)
      this.updateNavAvailability()
    }

    // Counted before Swiper runs so the total reflects the real products.
    this.slideCount = this.carouselEl.querySelectorAll('.swiper-slide').length

    const slidesPerViewMobile = parseSlidesPerView(
      this.dataset.slidesPerViewMobile,
      DEFAULT_SLIDES_PER_VIEW_MOBILE
    )
    const slidesPerViewDesktop = parseSlidesPerView(
      this.dataset.slidesPerView,
      DEFAULT_SLIDES_PER_VIEW
    )

    const config = {
      slidesPerView: slidesPerViewMobile,
      spaceBetween: 5,
      loop: true,
      watchSlidesProgress: true,
      a11y: {
        enabled: true,
        slideRole: 'group',
        itemRoleDescriptionMessage: 'slide',
        prevSlideMessage:
          this.prevBtn?.getAttribute('aria-label') || 'Previous slide',
        nextSlideMessage:
          this.nextBtn?.getAttribute('aria-label') || 'Next slide',
      },
      navigation: {
        nextEl: this.nextBtn,
        prevEl: this.prevBtn,
      },
      breakpoints: {
        750: {
          slidesPerView: slidesPerViewDesktop,
          spaceBetween: 20,
        },
      },
      on: {
        init: updateVisibility,
        slideChange: updateVisibility,
        transitionEnd: updateVisibility,
        resize: updateVisibility,
        breakpoint: updateVisibility,
        slidesLengthChange: updateVisibility,
      },
    }

    // Autoplay is started/stopped manually in syncAutoplay() so it only runs
    // while the section is in view and the shopper isn't interacting with it.
    if (this.autoplayEnabled) {
      config.autoplay = {
        delay:
          parseInt(this.dataset.autoplayDelay, 10) || DEFAULT_AUTOPLAY_DELAY,
        disableOnInteraction: false,
      }
    }

    this.swiper = new Swiper(this.carouselEl, config)
  }

  initAutoplay() {
    if (!this.autoplayEnabled || !this.swiper?.autoplay) return

    // Swiper starts autoplay on init; hold it until the section is in view.
    this.swiper.autoplay.stop()

    const sync = () => this.syncAutoplay()

    this.reducedMotion = window.matchMedia(REDUCED_MOTION_MQ)
    this.onReducedMotionChange = sync
    this.reducedMotion.addEventListener('change', this.onReducedMotionChange)

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.isInView = entry.isIntersecting
        sync()
      },
      { threshold: 0.25 }
    )
    this.observer.observe(this)

    const interactiveEl = this.regionEl || this.carouselEl
    interactiveEl.addEventListener('mouseenter', () => {
      this.isHovered = true
      sync()
    })
    interactiveEl.addEventListener('mouseleave', () => {
      this.isHovered = false
      sync()
    })
    // Keyboard focus anywhere in the section (incl. the Shop the Look tile
    // that precedes the carousel) pauses autoplay.
    this.addEventListener('focusin', () => {
      this.hasFocus = true
      sync()
    })
    this.addEventListener('focusout', (event) => {
      if (this.contains(event.relatedTarget)) return
      this.hasFocus = false
      sync()
    })

    // Manual navigation (arrows or swipe/drag) stops autoplay for the session
    // so it doesn't fight the shopper.
    const stopForSession = () => {
      this.userStopped = true
      sync()
    }
    this.prevBtn?.addEventListener('click', stopForSession)
    this.nextBtn?.addEventListener('click', stopForSession)
    this.swiper.on('sliderFirstMove', stopForSession)

    // Visible slide count changes across breakpoints.
    this.swiper.on('breakpoint', sync)
    this.swiper.on('resize', sync)
  }

  hasOverflow() {
    const slidesPerView = parseFloat(this.swiper?.params.slidesPerView)
    return this.slideCount > (Number.isFinite(slidesPerView) ? slidesPerView : 1)
  }

  syncAutoplay() {
    const autoplay = this.swiper?.autoplay
    if (!autoplay) return

    const shouldRun =
      this.isInView &&
      !this.isHovered &&
      !this.hasFocus &&
      !this.userStopped &&
      !this.reducedMotion?.matches &&
      this.hasOverflow()

    if (shouldRun && !autoplay.running) autoplay.start()
    else if (!shouldRun && autoplay.running) autoplay.stop()
  }

  // Hide off-screen slides from assistive tech and take their focusable
  // descendants out of the tab order; reveal visible ones. Swiper's
  // `swiper-slide-visible` marks exactly the on-screen slides, so we key on it
  // alone. In loop mode the visible slide may be a duplicate clone while its
  // off-screen original lacks the class (and gets hidden), so keying on
  // visibility exposes each on-screen product once without hiding what the user
  // can actually see.
  updateSlideVisibility(swiper) {
    const instance = swiper || this.swiper
    if (!instance || !instance.slides) return
    Array.prototype.forEach.call(instance.slides, (slide) => {
      const isVisible = slide.classList.contains('swiper-slide-visible')
      setSlideHidden(slide, !isVisible)
    })
  }

  updateNavAvailability() {
    if (!this.instructions) return

    const describedEl = this.regionEl || this.carouselEl
    const navButtons = [this.prevBtn, this.nextBtn]
    const isOperable = (btn) => !!(btn && btn.offsetParent !== null)
    const hasControls = isOperable(this.prevBtn) || isOperable(this.nextBtn)

    if (hasControls) {
      this.instructions.hidden = false
      describedEl?.setAttribute('aria-describedby', this.instructions.id)
      navButtons.forEach((btn) => {
        btn?.setAttribute('aria-describedby', this.instructions.id)
      })
    } else {
      describedEl?.removeAttribute('aria-describedby')
      navButtons.forEach((btn) => btn?.removeAttribute('aria-describedby'))
      this.instructions.hidden = true
    }
  }
}

customElements.define('stl-product-carousel', STLProductCarousel)
